/**
 * @file Renders the drawer form that creates a new entity item.
 */
import type { FormInstance } from "antd";

import { EntityForm, useForm } from "@components/Form";
import type { AppContext } from "@components/AppContext/types";
import type { Operation } from "@api";

import Drawer from "./Drawer";
import type { DrawerRef } from "./types";
import type { TableRef } from "../Modal/types";

type EntityFormPassthroughProps = {
  entity: string;
  operation: Operation;
  schema?: unknown;
  item?: unknown;
  request?: AppContext["request"];
  submitLabel?: string;
  onSubmit?: (parameters: unknown) => unknown;
  submitButtonProps?: Record<string, unknown>;
  successMessage?: string | boolean;
  failedMessage?: string | boolean;
  shouldOptimizeUpdateMutation?: boolean;
  expectedErrors?: string[];
  formProps?: Record<string, unknown>;
  hasSubmitButton?: boolean;
  onFailure?: () => void;
  onSubmitFailed?: () => void;
  onSubmitConfirm?: (parameters: unknown, action: () => unknown) => unknown;
  submitItemProps?: Record<string, unknown>;
};

type Props = {
  onSuccess?: (createdItem: unknown) => unknown;
  form?: FormInstance;
  width?: number | string;
  table?: TableRef;
  onOpen?: (target?: unknown) => void;
  title: string;
  drawer: DrawerRef;
} & EntityFormPassthroughProps;

/**
 * Renders a drawer with an entity form that creates an item and adds it
 * to the table on success.
 */
const CreateDrawer = ({
  onSuccess: onSuccessCustom = () => undefined,
  form: customForm = undefined,
  width = undefined,
  table = undefined,
  onOpen = () => undefined,
  title,
  drawer,
  ...otherEntityFormProps
}: Props): JSX.Element => {
  const form = useForm(customForm);

  /**
   * Resets the form fields when the drawer closes.
   */
  const onClose = () =>
    form.resetFields();

  /**
   * Runs the custom success handler, adds the created item to the
   * table and closes the drawer.
   */
  const onSuccess = async (createdItem: unknown) => {
    if (onSuccessCustom) {
      await onSuccessCustom(createdItem);
    }

    if (table) {
      table.current.addItem(createdItem);
    }

    drawer.current.close();
  };

  const drawerProps = {
    forceRender: true,
    destroyOnClose: true,
    width,
    title,
    drawer,
    onOpen,
    onClose,
  };

  const entityFormProps = {
    form,
    onSuccess,
    ...otherEntityFormProps,
  };

  return (
    <Drawer {...drawerProps}>
      <EntityForm {...entityFormProps} />
    </Drawer>
  );
};

export default CreateDrawer;
